import { useBeneficiarySubmissionContext } from '@/components/blocks/BeneficiarySubmissionContext';
import { CartSetupRender } from '@/components/blocks/CartRender';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { Img } from '@/components/blocks/MaterialImage';
import { OneClick } from '@/components/blocks/OneClick';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { HeaderMiniCartDropMenuItemV2 } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemV2';
import { HeaderMiniCartTourStart } from '@/components/content/Header/parts/HeaderMiniCartTourStart';
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useProductDetailsTour } from '@/data/Content/ProductDetailsTour';
import { useLocalization } from '@/data/Localization';
import { formatPrice } from '@/utils/formatPrice';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Drawer, Skeleton, Stack, Typography } from '@mui/material';
import React, { FC, Fragment } from 'react';

export const HeaderMiniCartDrawerMenuV2: FC<{
	onClose: () => void;
	onDeleteCart: () => Promise<void>;
}> = ({ onClose, onDeleteCart }) => {
	const CartLabels = useLocalization('MiniCart');
	const { data: order, orderItems } = useCart();
	const beneficiarySubmissionContext = useBeneficiarySubmissionContext();
	const { onFinishProductDetailsTour, onAddToCartProductDetailsTour } = useProductDetailsTour();

	const handleClose = () => {
		onFinishProductDetailsTour();
		onClose();
	};

	return (
		<CartSetupRender order={order} onDeleteCart={onDeleteCart} onClose={handleClose}>
			{({ productsGroup, loading, grandTotal, onEmptyCart, removeAll, products }) => (
				<Drawer
					open
					onClose={handleClose}
					anchor="bottom"
					PaperProps={{
						sx: {
							display: 'flex',
							maxHeight: '607px',
							width: '100%',
							borderTopLeftRadius: '10px',
							borderTopRightRadius: '10px',
							backgroundColor: '#FFFFFF',
						},
					}}
				>
					<HeaderMiniCartTourStart
						sx={{
							display: 'flex',
							flex: 1,
							width: '100%',
							flexDirection: 'column',
							overflow: 'auto',
						}}
						loading={(productsGroup?.length ?? 0) === 0}
						onAddToCartProductDetailsTour={onAddToCartProductDetailsTour}
						onFinishProductDetailsTour={onFinishProductDetailsTour}
					>
						{orderItems?.length > 0 ? (
							<Fragment>
								<ClearIcon
									sx={(theme) => ({
										fontSize: theme.typography.dpXl?.fontSize,
										cursor: 'pointer',
										position: 'absolute',
										top: '14px',
										right: '14px',
									})}
									onClick={onClose}
								/>
								<Box
									sx={{
										width: '100%',
										height: '100px',
										paddingTop: '30px',
										paddingBottom: '30px',
										paddingX: '30px',
										borderBottom: '1px solid #E5E5E5',
									}}
								>
									<Typography
										sx={{
											fontSize: '18px',
											color: '#2C2C2C',
											fontWeight: 800,
											display: 'flex',
											alignItems: 'center',
											marginBottom: '10px',
										}}
									>
										<Img
											src="/images/check-circle.png"
											alt="check-circle"
											sx={{ height: '24px', width: '24px', marginRight: '5px' }}
										/>
										{'AGREGASTE A TU PEDIDO'}
									</Typography>
									<Stack direction="row" alignItems="center">
										<Typography
											sx={{
												fontSize: '15px',
												color: '#44484B',
												marginRight: '11px',
											}}
										>
											{'(Hay '}
											<Typography
												component="span"
												sx={(theme) => ({
													fontWeight: theme.typography.fontWeightBold,
													display: 'inline-block',
												})}
											>
												{loading ? <Skeleton variant="text" width="22px" /> : products.length}
											</Typography>
											{' productos en tu pedido)'}
										</Typography>
										<OneClick
											wrapper="button-base"
											sx={{
												fontSize: '15px',
												color: '#2C2C2C',
												textDecoration: 'underline',
											}}
											disabled={loading}
											onClick={onEmptyCart}
										>
											{'Eliminar todo'}
										</OneClick>
									</Stack>
								</Box>

								<Stack
									spacing={2}
									sx={{
										width: '100%',
										flex: 1,
										paddingY: '15px',
										paddingX: '30px',
										minHeight: 0,
										boxSizing: 'border-box',
										overflowY: 'auto',
									}}
								>
									{!loading ? (
										<React.Fragment>
											{productsGroup.map((product, index) => (
												<HeaderMiniCartDropMenuItemV2
													key={product.code}
													partNumber={product.partNumber}
													thumbnail={product.thumbnail}
													name={product.name}
													price={product.price}
													size={product.size}
													href={product.href}
													quantity={product.quantity}
													variant="drawer"
													onRemove={product.onRemove}
													onAdd={product.onAdd}
													onRemoveAll={removeAll}
													isLast={productsGroup.length === index}
												/>
											))}
										</React.Fragment>
									) : (
										<HeaderMiniCartDropMenuItemSkeleton variant="drawer" />
									)}
								</Stack>

								<Box
									sx={{
										width: '100%',
										height: 'auto',
										backgroundColor: '#FAFBFB',
										borderTop: '1px solid #E4E4E3',
									}}
								>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										sx={{
											borderBottom: '1px solid #E4E4E3',
											paddingX: '30px',
											paddingY: '10px',
											height: '40px',
										}}
									>
										<Typography
											sx={{
												fontSize: '17px',
												fontWeight: 800,
												color: '#2C2C2C',
											}}
										>
											{'Total del pedido:'}
										</Typography>
										<Typography
											sx={{
												fontWeight: 800,
												fontSize: '17px',
												color: '#2C2C2C',
											}}
										>
											{loading ? (
												<Skeleton variant="text" width="100px" />
											) : (
												formatPrice('es-MX', 'MXN', grandTotal)
											)}
										</Typography>
									</Stack>
									<Box
										sx={{
											paddingX: '24px',
											paddingY: '10px',
											height: '73px',
											display: 'grid',
											gridTemplateColumns: '1fr 1fr',
											gap: '10px',
											backgroundColor: '#FFFFFF',
										}}
									>
										<DpButton
											id="mini-cart-view-full-cart-button"
											data-testid="mini-cart-view-full-cart-button"
											variant="unfocused"
											size="small"
											sx={{
												fontSize: '13px',
												padding: '13px',
												height: '50px',
											}}
											onClick={handleClose}
										>
											{'Seguir comprando'}
										</DpButton>
										<DpButton
											id="mini-cart-view-full-cart-button"
											data-testid="mini-cart-view-full-cart-button"
											variant="primary"
											disabled={productsGroup.length === 0 || loading}
											onClick={() => {
												onClose();
												beneficiarySubmissionContext.onOpen();
											}}
											sx={{
												fontSize: '13px',
												padding: '13px',
												height: '50px',
											}}
										>
											{'Enviar a mi distribuidor'}
										</DpButton>
									</Box>
								</Box>
							</Fragment>
						) : (
							<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
								<Typography variant="body1" align="center">
									{CartLabels.Empty.t()}
								</Typography>
							</Stack>
						)}
					</HeaderMiniCartTourStart>
				</Drawer>
			)}
		</CartSetupRender>
	);
};
